import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Plus, Edit2, Trash2, Copy, Send, Play, Pause, Mail, Clock, Zap, Users, TestTube, Eye } from "lucide-react";
import { safeFormat } from "@/lib/dateUtils";
import { ManualSendAutomationDialog } from "@/components/ManualSendAutomationDialog";

const TRIGGER_OPTIONS = [
  { value: 'BEFORE_APPOINTMENT', label: 'Before Appointment', icon: '⏰', group: 'Appointment' },
  { value: 'AFTER_APPOINTMENT', label: 'After Appointment', icon: '✅', group: 'Appointment' },
  { value: 'ON_CANCELLATION', label: 'On Cancellation', icon: '❌', group: 'Appointment' },
  { value: 'ON_NO_SHOW', label: 'On No Show', icon: '🚫', group: 'Appointment' },
  { value: 'DEPOSIT_NOT_PAID', label: 'Deposit Not Paid', icon: '💷', group: 'Appointment' },
  { value: 'DAYS_SINCE_LAST_VISIT', label: 'Days Since Last Visit', icon: '📅', group: 'Client' },
  { value: 'CLIENT_STATUS_CHANGED', label: 'Client Status Changed', icon: '🔄', group: 'Client' },
  { value: 'TOTAL_SPENT_THRESHOLD', label: 'Total Spent Threshold', icon: '💎', group: 'Client' },
  { value: 'NEW_CLIENT', label: 'New Client', icon: '🆕', group: 'Client' },
  { value: 'BROADCAST', label: 'Broadcast Campaign', icon: '📢', group: 'Manual' },
];

const DYNAMIC_FIELDS = [
  { label: 'Client First Name', value: '{{client.firstName}}' },
  { label: 'Client Last Name', value: '{{client.lastName}}' },
  { label: 'Client Email', value: '{{client.email}}' },
  { label: 'Client Status', value: '{{client.status}}' },
  { label: 'Total Spent', value: '{{client.totalSpent}}' },
  { label: 'Days Since Visit', value: '{{client.daysSinceVisit}}' },
  { label: 'Appointment Date', value: '{{appointment.date}}' },
  { label: 'Appointment Time', value: '{{appointment.time}}' },
  { label: 'Service Name', value: '{{appointment.serviceName}}' },
  { label: 'Appointment Price', value: '{{appointment.price}}' },
  { label: 'Booking Link', value: '{{booking.link}}' },
  { label: 'Rebook 3-Week Link', value: '{{booking.rebook3wkLink}}' },
  { label: 'Rebook 4-Week Link', value: '{{booking.rebook4wkLink}}' },
  { label: 'Rebook Buttons (3wk + 4wk)', value: '{{booking.rebookButtons}}' },
  { label: 'Salon Name', value: '{{salon.name}}' },
];

const emptyForm = {
  name: '', description: '', trigger_type: 'BROADCAST', offset_hours: 0,
  conditions: {}, subject: '', body: '', cc: 'elke.dallavenezia@gmail.com', bcc: '', active: true
};

export default function EmailAutomations() {
  const [automations, setAutomations] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({...emptyForm});
  const [sending, setSending] = useState(null);
  const [historyDialog, setHistoryDialog] = useState(null);
  const [history, setHistory] = useState([]);
  const [manualSendAuto, setManualSendAuto] = useState(null);
  // Preview recipients
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/automations`);
      setAutomations(res.data.automations || []);
    } catch (e) {  }
  };

  useEffect(() => { load(); }, []);

  // Normalize legacy single-string status into an array so the multi-select
  // chips work for older automations created before multi-status support.
  const editAutomation = (auto) => {
    const status = auto.conditions?.status;
    const normalized = {
      ...auto,
      conditions: {
        ...auto.conditions,
        status: status && typeof status === 'string' ? [status] : (Array.isArray(status) ? status : []),
      },
    };
    setForm(normalized);
    setDialog(auto);
  };

  const previewRecipients = async () => {
    setPreviewing(true);
    setPreviewData(null);
    try {
      const res = await axios.post(`${API}/automations/preview-recipients`, {
        id: dialog?.id,
        trigger_type: form.trigger_type,
        offset_hours: form.offset_hours,
        conditions: form.conditions,
      });
      setPreviewData(res.data);
    } catch (e) {
      toast.error('Failed to load preview');
    }
    setPreviewing(false);
  };

  const save = async () => {
    try {
      if (dialog === 'new') {
        await axios.post(`${API}/automations`, form);
        toast.success('Automation created');
      } else {
        await axios.put(`${API}/automations/${dialog.id}`, form);
        toast.success('Automation updated');
      }
      setDialog(null);
      load();
    } catch (e) { toast.error('Failed to save'); }
  };

  const toggleActive = async (auto) => {
    await axios.put(`${API}/automations/${auto.id}`, { active: !auto.active });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this automation?')) return;
    await axios.delete(`${API}/automations/${id}`);
    toast.success('Deleted');
    load();
  };

  const clone = async (id) => {
    await axios.post(`${API}/automations/${id}/clone`);
    toast.success('Automation cloned');
    load();
  };

  const sendTest = async (auto) => {
    setSending(auto.id);
    try {
      const res = await axios.post(`${API}/automations/${auto.id}/test`, { email: 'elke.dallavenezia@gmail.com' });
      if (res.data.success) toast.success('Test email sent!');
      else toast.error(res.data.error || 'Failed');
    } catch (e) { toast.error('Failed to send test'); }
    setSending(null);
  };

  const sendBroadcast = async (auto) => {
    if (!window.confirm(`Send this broadcast to all matching clients?`)) return;
    setSending(auto.id);
    try {
      const res = await axios.post(`${API}/automations/broadcast/${auto.id}`);
      toast.success(`Broadcast sent! ${res.data.sent} emails delivered, ${res.data.failed} failed`);
      load();
    } catch (e) { toast.error('Failed to send broadcast'); }
    setSending(null);
  };

  const viewHistory = async (autoId) => {
    const res = await axios.get(`${API}/automations/email-history?limit=50`);
    setHistory((res.data.history || []).filter(h => !autoId || h.automation_id === autoId));
    setHistoryDialog(true);
  };

  const insertField = (field) => {
    setForm(p => ({ ...p, body: p.body + field }));
  };

  const triggerColor = (t) => {
    if (t === 'BROADCAST') return 'bg-purple-100 text-purple-700';
    if (t.startsWith('BEFORE') || t.startsWith('AFTER')) return 'bg-blue-100 text-blue-700';
    if (t.startsWith('ON_')) return 'bg-red-100 text-red-700';
    return 'bg-green-100 text-green-700';
  };

  const needsOffset = (t) => ['BEFORE_APPOINTMENT', 'AFTER_APPOINTMENT', 'DEPOSIT_NOT_PAID', 'DAYS_SINCE_LAST_VISIT'].includes(t);

  return (
    <div className="space-y-6 fade-in">
      <Toaster position="top-center" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Playfair Display' }}>
            <Mail size={24} className="text-primary" /> Email Automations
          </h1>
          <p className="text-sm text-muted-foreground">{automations.length} automations · {automations.filter(a => a.active).length} active · <span className="text-green-500">Runner: every 15 min</span></p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              toast.info('Running automations...');
              const res = await axios.post(`${API}/automations/execute`);
              if (res.data.total_sent > 0) {
                toast.success(`Sent ${res.data.total_sent} emails`);
              } else {
                toast.info('No emails due right now');
              }
              load();
            } catch (e) { toast.error('Execution failed'); }
          }} className="rounded-xl">
            <Zap size={14} className="mr-1" /> Run Now
          </Button>
          <Button variant="outline" size="sm" onClick={() => viewHistory(null)} className="rounded-xl">
            <Clock size={14} className="mr-1" /> History
          </Button>
          <Button
            variant="outline" size="sm"
            data-testid="backfill-vip-comms-btn"
            onClick={async () => {
              try {
                const res = await axios.post(`${API}/rewards/backfill-comms`);
                if (res.data.success) {
                  const c = res.data.created, s = res.data.skipped_existing;
                  toast.success(c > 0 ? `Backfilled ${c} VIP reward comm${c !== 1 ? 's' : ''}${s ? ` (${s} already logged)` : ''}` : 'No new comms to backfill — all VIP rewards already logged');
                }
              } catch (e) { toast.error('Backfill failed'); }
            }}
            className="rounded-xl"
            title="Create email_history entries for any VIP rewards already issued"
          >
            ✨ Backfill VIP Comms
          </Button>
          <Button onClick={() => { setForm({...emptyForm}); setDialog('new'); }} className="rounded-xl">
            <Plus size={16} className="mr-1" /> New Automation
          </Button>
        </div>
      </div>

      {/* Automation Cards */}
      <div className="space-y-3">
        {automations.map(auto => (
          <Card key={auto.id} className={`p-4 rounded-2xl border shadow-sm hover:shadow-md transition-shadow ${!auto.active ? 'opacity-60' : ''}`}>
            <div className="flex items-center gap-4">
              <Switch checked={auto.active} onCheckedChange={() => toggleActive(auto)} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-sm" style={{ fontFamily: 'Playfair Display' }}>{auto.name}</h3>
                  <Badge className={`text-[10px] rounded-full ${triggerColor(auto.trigger_type)}`}>
                    {TRIGGER_OPTIONS.find(t => t.value === auto.trigger_type)?.icon} {auto.trigger_type.replace(/_/g, ' ')}
                  </Badge>
                  {auto.active ? <Badge className="bg-green-100 text-green-700 text-[10px] rounded-full">Active</Badge> : <Badge variant="outline" className="text-[10px] rounded-full">Paused</Badge>}
                </div>
                <p className="text-xs text-muted-foreground truncate">{auto.description || auto.subject}</p>
                <div className="flex items-center gap-4 mt-1 text-[10px] text-muted-foreground flex-wrap">
                  <span>{auto.sent_count} sent</span>
                  {auto.last_run && <span>Last run: {safeFormat(auto.last_run, 'd MMM HH:mm')}</span>}
                  {needsOffset(auto.trigger_type) && <span>Offset: {auto.offset_hours}h</span>}
                  {auto.trigger_type === 'DAYS_SINCE_LAST_VISIT' && (auto.last_run_sent !== undefined || auto.last_run_skipped_rebooked !== undefined) && (
                    <span className="text-emerald-600" title="Stats from the last run of this automation">
                      Last run · sent {auto.last_run_sent || 0}
                      {(auto.last_run_skipped_rebooked || 0) > 0 && (
                        <> · <span className="text-amber-600">skipped {auto.last_run_skipped_rebooked} (already rebooked)</span></>
                      )}
                      {(auto.last_run_skipped_cooldown || 0) > 0 && (
                        <> · skipped {auto.last_run_skipped_cooldown} (cooldown)</>
                      )}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                {auto.trigger_type === 'BROADCAST' && (
                  <Button variant="default" size="sm" onClick={() => sendBroadcast(auto)} disabled={sending === auto.id} className="rounded-lg h-8 text-xs">
                    <Send size={12} className="mr-1" /> {sending === auto.id ? 'Sending...' : 'Send Now'}
                  </Button>
                )}
                <Button variant="ghost" size="icon" onClick={() => setManualSendAuto(auto)} className="h-8 w-8 rounded-lg" title="Send to specific client(s)" data-testid={`manual-send-btn-${auto.id}`}><Users size={13} /></Button>
                <Button variant="ghost" size="icon" onClick={() => sendTest(auto)} disabled={sending === auto.id} className="h-8 w-8 rounded-lg" title="Send test"><TestTube size={13} /></Button>
                <Button variant="ghost" size="icon" onClick={() => editAutomation(auto)} className="h-8 w-8 rounded-lg" title="Edit"><Edit2 size={13} /></Button>
                <Button variant="ghost" size="icon" onClick={() => clone(auto.id)} className="h-8 w-8 rounded-lg" title="Clone"><Copy size={13} /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(auto.id)} className="h-8 w-8 rounded-lg text-destructive" title="Delete"><Trash2 size={13} /></Button>
              </div>
            </div>
          </Card>
        ))}
        {automations.length === 0 && (
          <Card className="p-12 rounded-2xl text-center">
            <Mail size={40} className="mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground">No automations yet. Create your first email automation!</p>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="rounded-2xl max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display' }}>{dialog === 'new' ? 'New Automation' : 'Edit Automation'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Basic info */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="mt-1 rounded-xl" placeholder="e.g. 24 Hour Reminder" /></div>
              <div className="col-span-2"><Label className="text-xs">Description</Label><Input value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} className="mt-1 rounded-xl" placeholder="What does this automation do?" /></div>
            </div>

            {/* Trigger */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Trigger Type</Label>
                <Select value={form.trigger_type} onValueChange={v => setForm(p => ({...p, trigger_type: v}))}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TRIGGER_OPTIONS.map(t => <SelectItem key={t.value} value={t.value}>{t.icon} {t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {needsOffset(form.trigger_type) && (
                <div>
                  <Label className="text-xs">Offset ({form.trigger_type === 'DAYS_SINCE_LAST_VISIT' ? 'days' : 'hours'})</Label>
                  <Input type="number" value={form.offset_hours} onChange={e => setForm(p => ({...p, offset_hours: parseInt(e.target.value) || 0}))} className="mt-1 rounded-xl" data-testid="automation-offset" />
                  {form.trigger_type === 'DAYS_SINCE_LAST_VISIT' && (
                    <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
                      Sends when a client passes this many days since their last visit. Re-checks every 15 min · 7-day cooldown per client so no one gets nagged.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Conditions — show only fields that actually apply to the selected trigger */}
            {form.trigger_type !== 'BROADCAST' && (() => {
              const isDaysSince  = form.trigger_type === 'DAYS_SINCE_LAST_VISIT';
              const isClientLvl  = ['CLIENT_STATUS_CHANGED', 'TOTAL_SPENT_THRESHOLD'].includes(form.trigger_type);
              const isApptLvl    = ['BEFORE_APPOINTMENT', 'AFTER_APPOINTMENT', 'ON_CANCELLATION', 'ON_NO_SHOW', 'DEPOSIT_NOT_PAID'].includes(form.trigger_type);
              // What to show for each trigger
              const showStatus      = isDaysSince || isClientLvl || isApptLvl;
              const showDaysSince   = false; // baked into Offset for DAYS_SINCE_LAST_VISIT; not used elsewhere
              const showSpent       = isDaysSince || isClientLvl;
              const showAppts       = isDaysSince || isClientLvl;
              if (!showStatus && !showSpent && !showAppts) return null;
              return (
                <div className="p-3 bg-muted/30 rounded-xl">
                  <Label className="text-xs font-semibold mb-1 block">Conditions (optional filters)</Label>
                  <p className="text-[10px] text-muted-foreground mb-2">Narrow this automation to a subset of clients. Leave blank for everyone.</p>
                  <div className="grid grid-cols-2 gap-3">
                    {showStatus && (
                      <div className="col-span-2">
                        <Label className="text-[10px]">Client Status (select one or more)</Label>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {['Active','VIP','Inactive','OnHold'].map(s => {
                            const sel = Array.isArray(form.conditions?.status) ? form.conditions.status : (form.conditions?.status ? [form.conditions.status] : []);
                            const isOn = sel.includes(s);
                            return (
                              <button
                                key={s}
                                type="button"
                                data-testid={`status-chip-${s}`}
                                onClick={() => {
                                  setForm(p => {
                                    const cur = Array.isArray(p.conditions?.status) ? [...p.conditions.status] : (p.conditions?.status ? [p.conditions.status] : []);
                                    const next = cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s];
                                    return { ...p, conditions: { ...p.conditions, status: next } };
                                  });
                                }}
                                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                                  isOn ? 'bg-primary text-white border-primary' : 'bg-card border-border text-muted-foreground hover:border-primary/40'
                                }`}
                              >
                                {s}
                              </button>
                            );
                          })}
                          {(Array.isArray(form.conditions?.status) ? form.conditions.status.length > 0 : !!form.conditions?.status) && (
                            <button
                              type="button"
                              onClick={() => setForm(p => ({ ...p, conditions: { ...p.conditions, status: [] } }))}
                              className="px-2 py-1 rounded-full text-[11px] text-muted-foreground hover:text-foreground underline"
                            >Clear</button>
                          )}
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1">Leave empty to include every status.</p>
                      </div>
                    )}
                    {showDaysSince && (
                      <div>
                        <Label className="text-[10px]">Min days since visit</Label>
                        <Input type="number" value={form.conditions?.days_since_min || ''} onChange={e => setForm(p => ({...p, conditions: {...p.conditions, days_since_min: e.target.value}}))} className="mt-1 rounded-xl h-8 text-xs" placeholder="e.g. 21" />
                      </div>
                    )}
                    {showSpent && (
                      <div>
                        <Label className="text-[10px]">Min total spent (£)</Label>
                        <Input type="number" value={form.conditions?.total_spent_min || ''} onChange={e => setForm(p => ({...p, conditions: {...p.conditions, total_spent_min: e.target.value}}))} className="mt-1 rounded-xl h-8 text-xs" placeholder="e.g. 100" />
                      </div>
                    )}
                    {showAppts && (
                      <div>
                        <Label className="text-[10px]">Min appointments</Label>
                        <Input type="number" value={form.conditions?.min_appointments || ''} onChange={e => setForm(p => ({...p, conditions: {...p.conditions, min_appointments: e.target.value}}))} className="mt-1 rounded-xl h-8 text-xs" placeholder="e.g. 3" />
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Email Template */}
            <div>
              <Label className="text-xs font-semibold">Email Template</Label>
              <div className="mt-2 space-y-3">
                <div>
                  <Label className="text-[10px]">Subject</Label>
                  <Input value={form.subject} onChange={e => setForm(p => ({...p, subject: e.target.value}))} className="mt-1 rounded-xl" placeholder="e.g. Reminder: Your appointment tomorrow 💅" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-[10px]">Body</Label>
                    <div className="flex flex-wrap gap-1">
                      {DYNAMIC_FIELDS.slice(0, 6).map(f => (
                        <button key={f.value} onClick={() => insertField(f.value)} className="text-[9px] px-1.5 py-0.5 bg-secondary rounded-lg hover:bg-primary/10 transition-colors" title={f.label}>
                          {f.label.split(' ').pop()}
                        </button>
                      ))}
                      <details className="relative inline-block">
                        <summary className="text-[9px] px-1.5 py-0.5 bg-secondary rounded-lg cursor-pointer hover:bg-primary/10">More...</summary>
                        <div className="absolute right-0 top-full mt-1 bg-card border rounded-xl shadow-lg p-2 z-10 min-w-[160px]">
                          {DYNAMIC_FIELDS.map(f => (
                            <button key={f.value} onClick={() => insertField(f.value)} className="block w-full text-left text-[10px] px-2 py-1 rounded-lg hover:bg-muted transition-colors">
                              {f.label}: <code className="text-primary">{f.value}</code>
                            </button>
                          ))}
                        </div>
                      </details>
                    </div>
                  </div>
                  <Textarea value={form.body} onChange={e => setForm(p => ({...p, body: e.target.value}))} className="mt-1 rounded-xl text-sm font-mono" rows={10} placeholder="Write your email body here. Use {{variables}} for personalization." />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px]">CC</Label><Input value={form.cc} onChange={e => setForm(p => ({...p, cc: e.target.value}))} className="mt-1 rounded-xl h-8 text-xs" /></div>
                  <div><Label className="text-[10px]">BCC</Label><Input value={form.bcc} onChange={e => setForm(p => ({...p, bcc: e.target.value}))} className="mt-1 rounded-xl h-8 text-xs" /></div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={v => setForm(p => ({...p, active: v}))} />
              <Label className="text-xs">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)} className="rounded-xl">Cancel</Button>
            <Button
              variant="outline"
              onClick={previewRecipients}
              disabled={previewing}
              className="rounded-xl gap-1.5"
              data-testid="preview-recipients-btn"
            >
              <Eye size={13} /> {previewing ? 'Loading…' : 'Preview Recipients'}
            </Button>
            <Button onClick={save} className="rounded-xl">Save Automation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Recipients Dialog */}
      <Dialog open={!!previewData} onOpenChange={(o) => { if (!o) setPreviewData(null); }}>
        <DialogContent className="rounded-2xl max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="preview-recipients-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display' }}>Recipient Preview</DialogTitle>
          </DialogHeader>
          {previewData && (
            <div className="space-y-4">
              {previewData.unsupported && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                  {previewData.notes?.[0]}
                </div>
              )}
              {!previewData.unsupported && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <Card className="p-3 rounded-2xl">
                      <p className="text-[10px] uppercase text-muted-foreground">Will receive</p>
                      <p className="text-2xl font-semibold text-emerald-600 tabular-nums" data-testid="preview-eligible-count">{previewData.counts.eligible}</p>
                    </Card>
                    {previewData.counts.skipped_already_rebooked !== undefined && (
                      <Card className="p-3 rounded-2xl">
                        <p className="text-[10px] uppercase text-muted-foreground">Already rebooked</p>
                        <p className="text-2xl font-semibold text-amber-600 tabular-nums">{previewData.counts.skipped_already_rebooked}</p>
                      </Card>
                    )}
                    {previewData.counts.skipped_cooldown !== undefined && (
                      <Card className="p-3 rounded-2xl">
                        <p className="text-[10px] uppercase text-muted-foreground">In cooldown</p>
                        <p className="text-2xl font-semibold text-muted-foreground tabular-nums">{previewData.counts.skipped_cooldown}</p>
                      </Card>
                    )}
                    {previewData.counts.skipped_no_email !== undefined && (
                      <Card className="p-3 rounded-2xl">
                        <p className="text-[10px] uppercase text-muted-foreground">No email on file</p>
                        <p className="text-2xl font-semibold text-muted-foreground tabular-nums">{previewData.counts.skipped_no_email}</p>
                      </Card>
                    )}
                    <Card className="p-3 rounded-2xl">
                      <p className="text-[10px] uppercase text-muted-foreground">Matched total</p>
                      <p className="text-2xl font-semibold tabular-nums">{previewData.counts.matched_total}</p>
                    </Card>
                  </div>

                  {previewData.notes?.length > 0 && (
                    <ul className="text-[11px] text-muted-foreground space-y-0.5 pl-4 list-disc">
                      {previewData.notes.map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                  )}

                  <Tabs defaultValue="eligible" className="w-full">
                    <TabsList>
                      <TabsTrigger value="eligible">
                        Will Receive ({previewData.eligible?.length || 0})
                      </TabsTrigger>
                      {(previewData.skipped_already_rebooked?.length || 0) > 0 && (
                        <TabsTrigger value="rebooked">Already Rebooked ({previewData.skipped_already_rebooked.length})</TabsTrigger>
                      )}
                      {(previewData.skipped_cooldown?.length || 0) > 0 && (
                        <TabsTrigger value="cooldown">In Cooldown ({previewData.skipped_cooldown.length})</TabsTrigger>
                      )}
                      {(previewData.skipped_no_email?.length || 0) > 0 && (
                        <TabsTrigger value="noemail">No Email ({previewData.skipped_no_email.length})</TabsTrigger>
                      )}
                    </TabsList>

                    {[
                      { v: 'eligible', list: previewData.eligible || [] },
                      { v: 'rebooked', list: previewData.skipped_already_rebooked || [] },
                      { v: 'cooldown', list: previewData.skipped_cooldown || [] },
                      { v: 'noemail',  list: previewData.skipped_no_email || [] },
                    ].map(({ v, list }) => (
                      <TabsContent key={v} value={v} className="mt-2">
                        {list.length === 0 ? (
                          <p className="text-xs text-muted-foreground italic py-3 text-center">No clients in this group.</p>
                        ) : (
                          <div className="border rounded-xl overflow-hidden">
                            <table className="w-full text-xs">
                              <thead className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
                                <tr>
                                  <th className="text-left p-2">Name</th>
                                  <th className="text-left p-2">Email</th>
                                  <th className="text-left p-2">Status</th>
                                  <th className="text-right p-2">Days Since</th>
                                  <th className="text-right p-2">Spent</th>
                                  <th className="text-right p-2">Visits</th>
                                </tr>
                              </thead>
                              <tbody>
                                {list.map((c, i) => (
                                  <tr key={c.id || i} className="border-t hover:bg-muted/30" data-testid={`preview-row-${v}-${i}`}>
                                    <td className="p-2 font-medium">{c.name || '—'}</td>
                                    <td className="p-2 text-muted-foreground break-all">{c.email || '—'}</td>
                                    <td className="p-2"><Badge variant="outline" className="rounded-full text-[10px]">{c.status || '—'}</Badge></td>
                                    <td className="p-2 text-right tabular-nums">{c.days_since ?? '—'}</td>
                                    <td className="p-2 text-right tabular-nums">£{(c.total_spent || 0).toFixed(0)}</td>
                                    <td className="p-2 text-right tabular-nums">{c.total_appointments || 0}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </TabsContent>
                    ))}
                  </Tabs>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPreviewData(null)} className="rounded-xl">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email History Dialog */}
      <Dialog open={!!historyDialog} onOpenChange={() => setHistoryDialog(null)}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Playfair Display' }}>Email History</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No emails sent yet</p>
            ) : history.map(h => {
              const isVip = h.automation_id === 'vip_reward' || h.automation_name === 'VIP Reward';
              const ok = h.status === 'sent' || h.status === 'sent_historical';
              const statusCls = ok ? 'bg-green-100 text-green-700'
                : h.status === 'failed' ? 'bg-red-100 text-red-700'
                : 'bg-amber-100 text-amber-800';
              return (
                <div key={h.id} className={`flex items-center justify-between p-3 rounded-xl ${isVip ? 'bg-amber-50 border border-amber-200' : 'bg-muted/30'}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {isVip && <Badge className="text-[10px] rounded-full bg-amber-100 text-amber-800 border border-amber-300">✨ VIP Reward</Badge>}
                      <p className="text-sm font-medium truncate">{h.subject}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">{h.client_name} ({h.client_email})</p>
                    <p className="text-[10px] text-muted-foreground">
                      {h.automation_name} · {safeFormat(h.created_at, 'd MMM yyyy HH:mm')}
                      {h.voucher_code && <span className="ml-2 font-mono">{h.voucher_code}</span>}
                    </p>
                  </div>
                  <Badge className={`text-[10px] rounded-full shrink-0 ${statusCls}`}>{h.status.replace(/_/g, ' ')}</Badge>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <ManualSendAutomationDialog automation={manualSendAuto} onClose={() => setManualSendAuto(null)} />
    </div>
  );
}
